import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { unidadesApi } from '../../api/client'

const MAX_BYTES = 10 * 1024 * 1024   // same cap as the backend

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// The files of one "archivo" cell: list, open, download, remove, attach.
// `archivos` is the cell's current list; `onChange(next)` reports the new one.
// `soloLectura`: open and download only (no Mantenimiento editing).
export default function ArchivosModal({ unidad, columna, archivos, onChange, onClose, api = unidadesApi, soloLectura = false }) {
  const { t } = useTranslation('engineering')
  const input = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const upload = async (files) => {
    setError('')
    const list = [...files]
    const tooBig = list.filter(f => f.size > MAX_BYTES)
    if (tooBig.length) setError(t('unidades.planilla.fileTooBig', { name: tooBig.map(f => f.name).join(', ') }))
    let next = [...archivos]
    setUploading(true)
    for (const f of list.filter(f => f.size <= MAX_BYTES && f.size > 0)) {
      try {
        next = [...next, await api.uploadArchivo(unidad.id, columna.nombre, f)]
        onChange(next)
      } catch {
        setError(t('unidades.planilla.uploadFailed', { name: f.name }))
      }
    }
    setUploading(false)
    if (input.current) input.current.value = ''
  }

  const remove = async (a) => {
    if (!confirm(t('unidades.planilla.deleteFileConfirm', { name: a.filename }))) return
    try {
      await api.deleteArchivo(a.id)
      onChange(archivos.filter(x => x.id !== a.id))
    } catch {
      setError(t('unidades.planilla.deleteFileFailed'))
    }
  }

  return (
    <Modal title={`${columna.nombre} — ${unidad.codigo}`} onClose={onClose}>
      <div className="space-y-4">
        {archivos.length === 0 ? (
          <p className="text-sm text-gray-400">{t('unidades.planilla.noFiles')}</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
            {archivos.map(a => (
              <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <button type="button" onClick={() => api.openArchivo(a).catch(() => setError(t('unidades.planilla.openFailed')))}
                  className="flex-1 min-w-0 text-left text-gray-800 hover:underline truncate" title={a.filename}>
                  {a.filename}
                </button>
                <span className="text-xs text-gray-400 shrink-0">{formatSize(a.size_bytes)}</span>
                <button type="button" onClick={() => api.downloadArchivo(a).catch(() => setError(t('unidades.planilla.openFailed')))}
                  className="text-xs font-medium text-gray-500 hover:text-gray-900 shrink-0">
                  {t('unidades.planilla.download')}
                </button>
                {!soloLectura && (
                  <button type="button" onClick={() => remove(a)}
                    className="text-xs font-medium text-gray-400 hover:text-red-500 shrink-0">
                    {t('unidades.delete')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {error && <p className="text-red-500 text-xs">{error}</p>}
        {!soloLectura && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">{t('unidades.planilla.fileLimit')}</p>
          <input ref={input} type="file" multiple className="hidden" onChange={e => upload(e.target.files)} />
          <button type="button" disabled={uploading} onClick={() => input.current?.click()}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors shrink-0">
            {uploading ? t('unidades.planilla.uploading') : t('unidades.planilla.attachFiles')}
          </button>
        </div>
        )}
      </div>
    </Modal>
  )
}
